import React, { memo, ReactNode, useCallback, useEffect, useState } from "react";
import { Wheel } from "./wheel";
import { useColumnsExtend } from "./columns-extend";
import { NativeProps, withNativeProps, mergeProps } from "@/utils/components-utils";
import { PickerProps } from "../picker";
import styles from "./picker-view.module.scss";
import Spin from "../Spin";
import useDebounceEffect from "@/hooks/useDebounceEffect";

export type PickerValue = string | null;

export type PickerValueExtend = {
	columns: PickerColumnItem[][];
	items: (PickerColumnItem | null)[];
};

export type PickerColumnItem = {
	label: ReactNode | string;
	value: string;
	key?: string;
};

export type PickerColumn = (string | PickerColumnItem)[];

export type PickerViewProps = {
	columns: PickerColumn[] | ((value: PickerValue[]) => PickerColumn[]);
	value?: PickerValue[];
	defaultValue?: PickerValue[];
	mouseWheel?: boolean;
	loading?: boolean;
	loadingContent?: ReactNode;
	onChange?: (value: PickerValue[], extend: PickerValueExtend) => void;
	active_bar?: ReactNode;
} & Pick<PickerProps, "renderLabel"> &
	NativeProps<"--height" | "--item-height" | "--item-font-size">;

const defaultProps = {
	defaultValue: [],
	renderLabel: (item: PickerColumnItem) => item.label,
	mouseWheel: false,
	loadingContent: (
		<div className={styles.rfPickerView_loading_content}>
			<Spin loading={true} />
		</div>
	),
};

export const PickerView = memo<PickerViewProps>((p) => {
	const props = mergeProps(defaultProps, p);

	const [innerValue, setInnerValue] = useState<PickerValue[]>(
		props.value === undefined ? props.defaultValue : props.value,
	);

	// Sync `value` to `innerValue`
	useEffect(() => {
		if (props.value === undefined) return; // Uncontrolled mode
		if (props.value === innerValue) return;
		setInnerValue(props.value);
	}, [props.value]);

	useEffect(() => {
		if (props.value === innerValue) return;
		const timeout = window.setTimeout(() => {
			if (props.value !== undefined && props.value !== innerValue) {
				setInnerValue(props.value);
			}
		}, 1000);
		return () => {
			window.clearTimeout(timeout);
		};
	}, [props.value, innerValue]);

	const extend = useColumnsExtend(props.columns, innerValue);
	const columns = extend.columns;

	useDebounceEffect(
		() => {
			if (props.value === innerValue) return;
			props.onChange?.(innerValue, extend);
		},
		[innerValue],
		{
			wait: 0,
			leading: false,
			trailing: true,
		},
	);

	const handleSelect = useCallback((val: PickerValue, index: number) => {
		setInnerValue((prev) => {
			const next = [...prev];
			next[index] = val;
			return next;
		});
	}, []);

	return withNativeProps(
		props,
		<div className={styles.rfPickerView}>
			{props.loading ? (
				props.loadingContent
			) : (
				<>
					{props.active_bar ? props.active_bar : <div className={styles.rfPickerView_active_bar}></div>}
					{columns.map((column, index) => (
						<Wheel
							key={index}
							index={index}
							column={column}
							value={innerValue[index]}
							onSelect={handleSelect}
							renderLabel={props.renderLabel}
							mouseWheel={props.mouseWheel}
						/>
					))}
					<div className={styles.rfPickerView_mask}>
						<div className={styles.rfPickerView_mask_top} />
						<div className={styles.rfPickerView_mask_middle} />
						<div className={styles.rfPickerView_mask_bottom} />
					</div>
				</>
			)}
		</div>,
	);
});

PickerView.displayName = "PickerView";
